import React, { useState, useEffect, useRef } from 'react';

interface CoinCharacterProps {
  imageUrl: string;
  symbol: string;
  performancePct: string;
  prevPerformancePct?: string;
  color: string;
  size?: number;
  isMyPick?: boolean;
  animate?: boolean;
  racePosition?: number;
  totalRacers?: number;
  /** Explicit tiebreaker flags — when provided, override position-based face logic */
  isRaceWinner?: boolean;
  isRaceLoser?: boolean;
}

function getEmotion(
  pct: number,
  racePosition?: number,
  totalRacers?: number,
  isRaceWinner?: boolean,
  isRaceLoser?: boolean,
): { face: string; trail: string } {
  if (racePosition !== undefined && totalRacers !== undefined && totalRacers > 1) {
    // Tiebreaker flags take precedence when provided
    if (isRaceWinner) return { face: '😄', trail: '🚀' };
    if (isRaceLoser) return { face: '😢', trail: '' };
    return { face: '😐', trail: '' };
  }
  if (pct >= 3) return { face: '😄', trail: '🚀' };
  if (pct >= 0.5) return { face: '🙂', trail: '' };
  if (pct <= -3) return { face: '😵', trail: '💀' };
  if (pct <= -0.5) return { face: '😟', trail: '' };
  return { face: '😐', trail: '' };
}

export const CoinCharacter: React.FC<CoinCharacterProps> = ({
  imageUrl,
  symbol,
  performancePct,
  prevPerformancePct,
  color,
  size = 52,
  isMyPick = false,
  animate = false,
  racePosition,
  totalRacers,
  isRaceWinner,
  isRaceLoser,
}) => {
  const pct = parseFloat(performancePct) || 0;
  const prevPct = parseFloat(prevPerformancePct ?? performancePct) || 0;
  const { face, trail } = getEmotion(pct, racePosition, totalRacers, isRaceWinner, isRaceLoser);

  const [reactionEmoji, setReactionEmoji] = useState<string | null>(null);
  const prevRef = useRef(prevPct);

  useEffect(() => {
    const diff = pct - prevRef.current;
    if (Math.abs(diff) > 1) {
      setReactionEmoji(diff > 0 ? '🔥📈' : '😰📉');
      const t = setTimeout(() => setReactionEmoji(null), 1500);
      prevRef.current = pct;
      return () => clearTimeout(t);
    }
    prevRef.current = pct;
  }, [pct]);

  return (
    <div
      className="relative flex flex-col items-center"
      style={{ width: size + 20 }}
    >
      {/* Reaction burst */}
      {reactionEmoji && (
        <div
          className="absolute -top-8 left-1/2 text-sm font-bold z-10 animate-fade-in"
          style={{
            transform: 'translateX(-50%)',
            animation: 'fadeUp 1.5s ease-out forwards',
            whiteSpace: 'nowrap',
          }}
        >
          {reactionEmoji}
        </div>
      )}

      {/* Character circle */}
      <div
        className="relative rounded-full flex items-center justify-center overflow-hidden"
        style={{
          width: size,
          height: size,
          background: `${color}33`,
          border: `3px solid ${isMyPick ? '#fbbf24' : color}`,
          boxShadow: isMyPick
            ? `0 0 16px #fbbf24, 0 0 32px ${color}55`
            : `0 0 10px ${color}55`,
          animation: animate ? 'coinBounce 0.6s ease-in-out infinite alternate' : undefined,
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={symbol}
            className="w-full h-full object-cover rounded-full"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <span className="text-xs font-bold" style={{ color }}>
            {symbol.slice(0, 3)}
          </span>
        )}

        {/* Emotion overlay */}
        <div
          className="absolute bottom-0 right-0 text-xs leading-none"
          style={{ fontSize: size * 0.3, filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))' }}
        >
          {face}
        </div>
      </div>

      {/* Trail effect — kept within the component's own bounding box to avoid
          clipping by the lane container's overflow-hidden */}
      {trail && (
        <div className="absolute -right-1 top-1/2 -translate-y-1/2 text-xs leading-none">
          {trail}
        </div>
      )}

      {/* Symbol label */}
      <div
        className="mt-1 text-xs font-bold text-center truncate"
        style={{ maxWidth: size + 16, color }}
      >
        {symbol}
      </div>

      {isMyPick && (
        <div
          className="mt-0.5 text-xs font-bold text-center px-1 rounded-full"
          style={{ backgroundColor: '#fbbf24', color: '#000', fontSize: 9 }}
        >
          YOUR PICK
        </div>
      )}
    </div>
  );
};
